import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { AiOutlineEnter } from 'react-icons/ai';
import { AuthorAvatar } from '@Components/AuthorAvatar';
import { Button } from '@Components/Button';
import { emailSelector, fullNameSelector } from '@src/redux/user';
import { COMMENT_BODY_MAX } from '../activity-feed-constants';
import { ActivityId } from '../activity-feed-data-utils';
import { usePostEventComment } from '../useOracleEventMutations';
import { CommentEditor } from './CommentEditor';
import { useCommentDraft } from './useCommentDraft';

interface CommentsBoxProps {
  activityId: ActivityId;
}

export const CommentsBox = ({ activityId }: CommentsBoxProps): ReactNode => {
  const fullName = useSelector(fullNameSelector);
  const email = useSelector(emailSelector);
  const { mutateAsync: postComment, isPending } = usePostEventComment();

  const { draft, setDraft, trimmed, handleSubmit } = useCommentDraft(
    '',
    async body => {
      // Snapshotted so tombstones can render "Deleted note by <name>" without a
      // users-table lookup. `||` (not `??`) because fullName seeds to '' pre-/me-fetch,
      // and a whitespace-only trim also drops to the email fallback.
      const displayName = fullName.trim() || email;
      await postComment({ activityId, body: { body, displayName } });
    },
    'post comment'
  );
  const canPost = trimmed.length > 0 && trimmed.length <= COMMENT_BODY_MAX && !isPending;
  const handlePost = () => {
    if (canPost) {
      handleSubmit();
    }
  };

  return (
    <div className='activity-feed-detail-comments'>
      <div className='activity-feed-detail-comments-input-row'>
        <AuthorAvatar displayName={fullName.trim() || null} email={email} />
        <CommentEditor
          markdown={draft}
          placeholder='Leave a note'
          canSubmit={canPost}
          onChange={setDraft}
          onSubmit={handlePost}
        />
      </div>
      <div className='activity-feed-detail-comments-actions'>
        <Button
          disabled={!canPost}
          loading={isPending}
          onClick={handlePost}
          trailingElement={<AiOutlineEnter />}
        >
          Post
        </Button>
      </div>
    </div>
  );
};
